import Swal from "sweetalert2";
import i18n from "@/i18n/index";
import router from "@/router/index";
import { clubUUID, userUUID, loggedIn } from "@/shared/state";
const Toast = Swal.mixin({
  toast: true,
  position: "top-right",
  showConfirmButton: false,
  timer: 12000,
  timerProgressBar: true,
});
export function useAuthenticatedFetch() {
  const token = localStorage.getItem("authToken");
  const authenticatedFetch = async (
    url: string,
    options: RequestInit = {},
    alert: boolean,
    customToastMessage: string | "",
    trace: boolean,
  ) => {
    const headers = new Headers(options.headers || {});

    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    headers.set("Accept", "application/json");
    headers.set("Content-Type", "application/json");
    if (trace) headers.set("X-Trace", "true");

    const response = await fetch(url, {
      ...options,
      headers,
    });
    if (!response.ok) {
      const responseJson = await response.json();

      if (response.status === 422) {
        // If the backend sends a simple top-level string message that isn't the standard validation wrapper
        if (
          responseJson?.message &&
          responseJson.message !== "The given data was invalid."
        ) {
          Toast.fire({
            title: i18n.t(
              "message.toast.invalidExtention." + responseJson.message,
            ),
            icon: "error",
          });
        } else if (responseJson?.errors) {
          let MessageError = "";

          Object.keys(responseJson.errors).forEach((attribute) => {
            const attributeErrors = responseJson.errors[attribute];

            if (Array.isArray(attributeErrors)) {
              attributeErrors.forEach((item) => {
                // Translate the field name (e.g., "tele" -> "Téléphone")
                const inputName = i18n.t("validation.attributes." + attribute);
                const parts = item.split(".");

                let translationKey = "";
                let valueParam = null;

                // Handle structures like "validation.unique" or "validation.max.string"
                if (parts[0] === "validation") {
                  translationKey = "validation." + parts.slice(1).join(".");
                  // If there's an extra trailing value parameter in the string array
                  valueParam = parts[2] || null;
                } else {
                  // Fallback for raw rule names sent directly
                  translationKey = "validation." + item;
                }

                // Generate translation with fallback formatting
                const translatedMessage = i18n.t(translationKey, {
                  attribute: inputName,
                  value: valueParam,
                });

                // Build the list cleanly
                MessageError += `- ${translatedMessage}\n`;
              });
            }
          });

          Toast.fire({
            title: MessageError.trim() || i18n.t("The given data was invalid."),
            icon: "error",
          });
        }
      } else if (response.status === 404) {
        Toast.fire({
          title: i18n.t(
            "message.not_found." + responseJson?.message + ".title",
          ),
          icon: "error",
        });
      } else if (response.status === 500) {
        Toast.fire({
          title: "erreur de connexion, essai plus tard ou actualisé votre page",
          icon: "error",
        });
      } else if (response.status === 402) {
        Toast.fire({
          title: responseJson?.error ?? responseJson?.message,
          icon: "error",
        });
      } else if (response.status === 400) {
        const rawMessage = responseJson?.message;
        const translationKey = "message.toast.invalidExtention." + rawMessage;

        // Check if the translation key actually exists in your i18n files
        const displayMessage = i18n.t(translationKey)
          ? i18n.t(translationKey) // Use the translation if it exists
          : rawMessage; // Otherwise, display the raw message directly to the admin

        Toast.fire({
          title: displayMessage || "Bad Request",
          icon: "error",
        });
      } else if (response.status === 401) {
        if (responseJson?.message === "Unauthenticated.") {
          loggedIn.value = false;
          localStorage.removeItem("user");
          localStorage.removeItem("authToken");
          localStorage.removeItem("selectedUserUUID");
          localStorage.removeItem("selectedClub");
          userUUID.value = null;
          clubUUID.value = null;

          router.push({ name: "login" });
        }
      }
      throw new Error(`Request failed: ${response.statusText}`);
    } else {
      if (response.status === 201 && alert && options?.method === "POST") {
        if (customToastMessage !== "") {
          Toast.fire({
            title: customToastMessage,
            icon: "success",
          });
        } else {
          Toast.fire({
            title: i18n.t("message.toast.add.title"),
            icon: "success",
          });
        }
      }
      if (response.status === 200 && alert && options?.method === "DELETE") {
        Toast.fire({
          title: i18n.t("message.toast.delete.title"),
          icon: "success",
        });
      }
      if (response.status === 200 && alert && options?.method === "PUT") {
        Toast.fire({
          title: i18n.t("message.toast.edit.title"),
          icon: "success",
        });
      }
    }
    return response.json();
  };

  return { authenticatedFetch };
}
